import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import { Building2, UserRound } from 'lucide-react';
import { AUTH_SHELL_VARIANTS, LOGIN_ACCOUNT_TYPES } from '@/constants/login';
import type { LoginAccountType } from '@/types/accountType';
import { useAuth } from '../hooks/useAuth';
import ForgotPasswordModal from '@/components/ForgotPasswordModal';
import { AuthShell } from '@/components/auth/AuthShell';
import {
  AccountTypeTabs,
  AuthError,
  AuthField,
  AuthFormHead,
  AuthInlineLink,
  AuthSecondaryLink,
  AuthSubmitButton,
} from '@/components/auth/AuthControls';
import { CandidateOAuthButtons } from '@/components/auth/CandidateOAuthButtons';
import { Button } from '@/components/ui/button';
import { getErrorMessage } from '@/lib/errorTranslator';
import {
  companySignupUrl,
  initialAccountType,
  offersCandidateSignup,
  showsAccountTypeTabs,
} from '@/lib/loginAccountType';
import { careersApi } from '@/careers/lib/careersApi';
import { saveCandidateSession } from '@/careers/lib/session';
import { candidateSignupPath, safeNextPath } from '@/careers/lib/redirect';
import { CAREERS_ROUTES } from '@/careers/constants';
import { getHomePath } from '@/lib/accountType';
import { careersErrorMessage } from '@/careers/lib/careersErrorMessage';

const LoginPage = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [accountType, setAccountType] = useState<LoginAccountType>(() => initialAccountType(search));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isForgotOpen, setIsForgotOpen] = useState(false);
  const isCandidate = accountType === LOGIN_ACCOUNT_TYPES.candidate;
  const candidateNext = safeNextPath(search);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (isCandidate) {
        saveCandidateSession(await careersApi.login(email, password));
        navigate(candidateNext ?? CAREERS_ROUTES.dashboard, { replace: true });
        return;
      }
      // Always land on the home page, never the page visited before sign-in:
      // dashboard for HR users, positions for listing-only companies.
      const user = await login(email, password);
      navigate(getHomePath(user), { replace: true });
    } catch (err) {
      setError(isCandidate ? careersErrorMessage(err) : getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      variant={AUTH_SHELL_VARIANTS.login}
      footer={
        <Trans
          i18nKey='auth.login.legal'
          components={{
            terms: <AuthInlineLink href='/terms-and-conditions'>{null}</AuthInlineLink>,
            privacy: <AuthInlineLink href='/privacy'>{null}</AuthInlineLink>,
          }}
        />
      }
    >
      <form onSubmit={handleSubmit}>
        <AuthFormHead title={t('auth.login.title')} subtitle={t('auth.login.subtitle')} />
        {showsAccountTypeTabs() && (
          <AccountTypeTabs
            value={accountType}
            onChange={(type) => {
              setAccountType(type);
              setError(null);
            }}
          />
        )}

        <div className='mt-[22px]'>{isCandidate && <CandidateOAuthButtons />}</div>

        <AuthField
          label={t('auth.login.emailLabel')}
          type='email'
          autoComplete='email'
          required
          value={email}
          onChange={setEmail}
        />
        <AuthField
          label={t('auth.passwordLabel')}
          type='password'
          autoComplete='current-password'
          required
          value={password}
          onChange={setPassword}
        />

        <div className='flex min-h-5 justify-end'>
          <Button
            type='button'
            variant='link'
            onClick={() => setIsForgotOpen(true)}
            className='h-auto p-0 text-[13.5px] font-semibold text-public-accent'
          >
            {t('auth.login.forgotPassword')}
          </Button>
        </div>

        <AuthError message={error} />
        <AuthSubmitButton
          label={t(isCandidate ? 'auth.login.submitCandidate' : 'auth.login.submitEmployer')}
          loading={loading}
          loadingLabel={t('auth.signingIn')}
          note={t('auth.notes.login')}
        />

        <div className='mt-[30px] border-t border-[var(--public-line-soft)] pt-[22px]'>
          <p className='mb-3.5 text-center text-[13.5px] text-public-muted'>{t('auth.login.newHere')}</p>
          {/* Peoply's site with the listings on their own domain offers no candidate account. */}
          <div
            data-single={!offersCandidateSignup()}
            className='grid gap-3 sm:grid-cols-2 data-[single=true]:sm:grid-cols-1'
          >
            {offersCandidateSignup() && (
              <AuthSecondaryLink
                to={candidateSignupPath(candidateNext)}
                icon={<UserRound className='h-[17px] w-[17px]' strokeWidth={1.8} aria-hidden='true' />}
              >
                {t('auth.login.createCandidate')}
              </AuthSecondaryLink>
            )}
            <AuthSecondaryLink
              to={companySignupUrl()}
              icon={<Building2 className='h-[17px] w-[17px]' strokeWidth={1.8} aria-hidden='true' />}
            >
              {t('auth.login.createCompany')}
            </AuthSecondaryLink>
          </div>
        </div>
      </form>

      <ForgotPasswordModal isOpen={isForgotOpen} onClose={() => setIsForgotOpen(false)} candidate={isCandidate} />
    </AuthShell>
  );
};
export default LoginPage;
